import { ContentfulLivePreviewProvider, useContentfulLiveUpdates } from "@contentful/live-preview/react";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import React, { ComponentProps, useEffect, useState } from "react";
import { KDevHead } from "@/components/Head/KDevHead";
import { Layout } from "@/components/Layout/Layout";
import { useDayjsToString } from "@/components/useDayjsToString";
import { TechBlog } from "@/lib/contentful/contentModel/techBlog";
import { getImageUrl } from "@/lib/contentful/util";
import { Banner } from "../../Banner/Banner";
import { TableOfContent } from "../../TableOfContent/TableOfContent";
import { TableOfContentItem } from "../../TableOfContent/type";
import { Ranking } from "../Ranking/Ranking";
import { Article } from "./Article/Article";
import { RelatedPosts } from "./RelatedPosts/RelatedPosts";
import { RightSideRelatedPosts } from "./RightSideRelatedPosts/RightSideRelatedPosts";

interface Props {
  post: TechBlog;
  relatedPosts: ComponentProps<typeof RelatedPosts>["posts"];
  tableOfContent: TableOfContentItem[];
  bodyHtml: string;
}

export const Slug: React.FC<Props> = ({ post, relatedPosts, tableOfContent, bodyHtml }) => {
  const [uniquePageviews, setViewCount] = useState(0);
  const router = useRouter();
  const pagePath = router.asPath;

  useEffect(() => {
    void fetch(window.location.origin + `/api/fetchData?pagePath=${pagePath}`).then(async (res) => {
      setViewCount((await res.json()) as number);
    });
  }, [pagePath]);
  const date = useDayjsToString(dayjs(post.fields.importedAt ?? post.sys.createdAt), "YYYY/MM/DD");
  const data = useContentfulLiveUpdates(post);

  return (
    <ContentfulLivePreviewProvider
      locale="en-US"
      enableInspectorMode={false}
      enableLiveUpdates={true}
      debugMode={false}
    >
      <KDevHead
        title={`${data.fields.title} - k.dev`}
        description={data.fields.body || (data.fields.description ?? data.fields.title) || ""}
        imageAlt={data.fields.image?.fields.title}
        image={getImageUrl(data.fields.image)}
      />
      <Layout isArticle>
        <div className="article-container bg-linear-gradient !px-[4%] !py-[24px]">
          <div>
            <div className="flex gap-6">
              <div className="hidden w-1/5 xlp:block">
                <TableOfContent tableOfContent={tableOfContent} body={data.fields.body}></TableOfContent>
              </div>
              <div className="w-full shrink-0 lg:w-2/3 xlp:w-3/5">
                <Article
                  post={data}
                  date={date}
                  tableOfContent={tableOfContent}
                  uniquePageviews={uniquePageviews}
                  bodyHtml={bodyHtml}
                />
              </div>
              <div className="hidden h-[fit-content] w-1/3 lg:block xlp:w-1/5">
                <Banner></Banner>
                <Ranking className="pt-[16px]" />
                {relatedPosts.length ? <RightSideRelatedPosts posts={relatedPosts} /> : null}
              </div>
            </div>
          </div>
          <div className="mx-auto w-full xlp:w-[60%]">
            {relatedPosts.length ? <RelatedPosts posts={relatedPosts} /> : null}
          </div>
        </div>
      </Layout>
    </ContentfulLivePreviewProvider>
  );
};
